import { useState } from "preact/hooks";
import type { Ctx } from "../app";
import { must, sb } from "../lib/supabase";
import { Badge, Card, ErrorBox, Loading, Modal, fmtDate, formData, go, toast, useAction, useLoad } from "../lib/ui";
import { APP_STATUSES, type Application } from "../lib/types";
import { followUpDraft, mailtoHref } from "../../../src/shared/templates";

const SELECT = "*, providers(first_name, last_name, npi, caqh_id), payers(name, slug), legal_entities(legal_name, group_npi, tin_last4)";

const CLOSED = new Set(["effective", "denied", "withdrawn"]);
// Board columns (closed outcomes are grouped at the end).
const BOARD: Array<[string, string[]]> = [
  ["Not started", ["not_started"]], ["Collecting docs", ["collecting_docs"]], ["Ready to submit", ["ready_to_submit"]],
  ["Submitted", ["submitted"]], ["In review", ["in_review"]], ["Info requested", ["info_requested"]],
  ["Approved / contracted", ["approved", "contracted"]], ["Effective", ["effective"]], ["Blocked", ["blocked"]],
];
const today = () => new Date().toISOString().slice(0, 10);
const daysSince = (d: string) => Math.round((Date.now() - new Date(d.length === 10 ? `${d}T12:00:00` : d).getTime()) / 86400000);

export function ApplicationsPage({ ctx, openId }: { ctx: Ctx; openId?: string }) {
  const [view, setView] = useState<"table" | "board">(() => { try { return (localStorage.getItem("appsView") as "board") ?? "table"; } catch { return "table"; } });
  const [status, setStatus] = useState("open");
  const [payer, setPayer] = useState("");
  const [owner, setOwner] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const { busy, run } = useAction();
  const ro = ctx.role === "viewer";
  const { data, error, reload } = useLoad(async () => {
    const [apps, payers, members, est] = await Promise.all([
      sb().from("applications").select(SELECT).eq("org_id", ctx.orgId).order("updated_at", { ascending: false }),
      sb().from("payers").select("id, name").order("name"),
      sb().from("org_members").select("user_id, display_name, email, role").eq("org_id", ctx.orgId).eq("active", true).neq("role", "viewer"),
      sb().from("v_application_estimates").select("application_id, expected_decision_on, median_days, sample_size").eq("org_id", ctx.orgId),
    ]);
    return { apps: must(apps) as Application[], payers: must(payers) as Array<{ id: string; name: string }>,
      members: must(members) as Array<{ user_id: string; display_name: string | null; email: string }>,
      est: new Map((must(est) as any[]).map((e) => [e.application_id, e])) };
  }, [ctx.orgId]);

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const name = (uid?: string | null) => { const m = data.members.find((x) => x.user_id === uid); return m ? m.display_name || m.email : ""; };
  const shown = data.apps.filter((a) =>
    (view === "board" || status === "all" || (status === "open" ? !CLOSED.has(a.status) : a.status === status)) &&
    (!payer || a.payer_id === payer) &&
    (!owner || (owner === "me" ? a.assignee === ctx.userId : owner === "none" ? !a.assignee : a.assignee === owner)) &&
    `${a.providers?.first_name} ${a.providers?.last_name} ${a.payers?.name}`.toLowerCase().includes(q.toLowerCase()));
  const setV = (v: "table" | "board") => { setView(v); try { localStorage.setItem("appsView", v); } catch { /* private mode */ } };

  /** Status change from the board: asks only for what the database requires. */
  const move = (a: Application, to: string) => {
    if (a.status === to || ro) return;
    const patch: Record<string, unknown> = { status: to };
    if (to === "blocked") {
      const why = prompt(`Why is ${a.providers?.first_name}'s ${a.payers?.name} application blocked?`);
      if (!why) return;
      patch.blocked_reason = why;
    }
    if (to === "submitted" && !a.submitted_on) patch.submitted_on = today();
    if ((to === "approved" || to === "contracted") && !a.approved_on) patch.approved_on = today();
    if (to === "effective" && !a.effective_on) {
      const d = prompt("Effective date (YYYY-MM-DD) — as stated by the payer:", today());
      if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
      patch.effective_on = d;
    }
    run(async () => { must(await sb().from("applications").update(patch).eq("id", a.id)); reload(); }, `Moved to ${to.replaceAll("_", " ")}`);
  };
  const assign = (ids: string[], uid: string) => run(async () => {
    must(await sb().from("applications").update({ assignee: uid || null }).in("id", ids));
    setPicked(new Set()); reload();
  }, uid ? `Assigned to ${name(uid)}` : "Unassigned");

  const Expected = ({ a }: { a: Application }) => {
    const e = data.est.get(a.id);
    if (!e?.expected_decision_on) return <span class="muted">—</span>;
    const late = e.expected_decision_on < today();
    return <span class={late ? "badge warn" : ""} title={`Median ${e.median_days} days for this payer, from your last ${e.sample_size} approvals`}>{fmtDate(e.expected_decision_on)}{late ? " (late)" : ""}</span>;
  };

  return (
    <Card title={`Applications (${shown.length})`} actions={<>
      <div class="seg" role="group" aria-label="View">
        <button class={view === "table" ? "on" : ""} onClick={() => setV("table")}>Table</button>
        <button class={view === "board" ? "on" : ""} onClick={() => setV("board")}>Board</button>
      </div>
      <input placeholder="BCBA or payer" value={q} onInput={(e) => setQ(e.currentTarget.value)} />
      {view === "table" && <select value={status} onChange={(e) => setStatus(e.currentTarget.value)} aria-label="Status filter">
        <option value="open">Open</option><option value="all">All</option>
        {APP_STATUSES.map((s) => <option value={s}>{s.replaceAll("_", " ")}</option>)}
      </select>}
      <select value={payer} onChange={(e) => setPayer(e.currentTarget.value)} aria-label="Payer filter">
        <option value="">All payers</option>{data.payers.map((p) => <option value={p.id}>{p.name}</option>)}
      </select>
      <select value={owner} onChange={(e) => setOwner(e.currentTarget.value)} aria-label="Assigned to">
        <option value="">Anyone</option><option value="me">Assigned to me</option><option value="none">Unassigned</option>
        {data.members.map((m) => <option value={m.user_id}>{m.display_name || m.email}</option>)}
      </select>
    </>}>
      <p class="muted small">Start applications from a BCBA's page (“Add payers”). One row per BCBA × payer × entity.
        {view === "board" && !ro && " Drag a card to another column to change its status."}</p>
      {view === "table" ? <>
        {picked.size > 0 && !ro && <div class="bulkbar">{picked.size} selected ·
          <select aria-label="Assign selected" onChange={(e) => { const v = e.currentTarget.value; if (v !== "_") assign([...picked], v === "none" ? "" : v); }}>
            <option value="_">Assign to…</option><option value="none">Nobody</option>
            {data.members.map((m) => <option value={m.user_id}>{m.display_name || m.email}</option>)}
          </select>
          <button class="ghost small" onClick={() => setPicked(new Set())}>Clear</button></div>}
        <table>
          <thead><tr>{!ro && <th><input type="checkbox" aria-label="Select all" checked={shown.length > 0 && shown.every((a) => picked.has(a.id))}
            onChange={(e) => setPicked(e.currentTarget.checked ? new Set(shown.map((a) => a.id)) : new Set())} /></th>}
            <th>BCBA</th><th>Payer</th><th>Status</th><th>Owner</th><th>Submitted</th><th>Expected decision</th><th>Next follow-up</th><th>Effective</th><th>Backdate</th></tr></thead>
          <tbody>{shown.map((a) => {
            const overdue = a.next_follow_up_on && a.next_follow_up_on < today();
            const stalled = !CLOSED.has(a.status) && a.status !== "not_started" && daysSince(a.last_event_at) >= 21;
            return (
              <tr class="click" onClick={() => go(`applications/${a.id}`)}>
                {!ro && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Select" checked={picked.has(a.id)} onChange={(e) => {
                  const n = new Set(picked); e.currentTarget.checked ? n.add(a.id) : n.delete(a.id); setPicked(n);
                }} /></td>}
                <td>{a.providers?.last_name}, {a.providers?.first_name}</td>
                <td>{a.payers?.name}<div class="muted small">{a.legal_entities?.legal_name ?? ""}</div></td>
                <td><Badge value={a.status} />{stalled && <span class="badge warn" title="No activity logged in 21+ days">stalled {daysSince(a.last_event_at)}d</span>}</td>
                <td class="small">{name(a.assignee) || <span class="muted">—</span>}</td>
                <td>{fmtDate(a.submitted_on)}</td><td><Expected a={a} /></td>
                <td>{overdue ? <span class="badge bad">{fmtDate(a.next_follow_up_on)}</span> : fmtDate(a.next_follow_up_on)}</td>
                <td>{fmtDate(a.effective_on)}</td>
                <td>{a.backdate_requested ? <Badge value={a.backdate_outcome ?? "pending"} /> : "—"}</td>
              </tr>);
          })}</tbody>
        </table>
      </> : (
        <div class="board">{BOARD.map(([label, statuses]) => {
          const cards = shown.filter((a) => statuses.includes(a.status));
          return (
            <section class="col" onDragOver={(e) => { if (!ro) { e.preventDefault(); (e.currentTarget as HTMLElement).classList.add("over"); } }}
              onDragLeave={(e) => (e.currentTarget as HTMLElement).classList.remove("over")}
              onDrop={(e) => {
                (e.currentTarget as HTMLElement).classList.remove("over");
                const a = data.apps.find((x) => x.id === e.dataTransfer?.getData("text/plain"));
                if (a && !statuses.includes(a.status)) move(a, statuses[0]!);
              }}>
              <h3>{label} <span class="muted">{cards.length}</span></h3>
              {cards.map((a) => {
                const stalled = !CLOSED.has(a.status) && a.status !== "not_started" && daysSince(a.last_event_at) >= 21;
                return (
                  <article class={`kcard ${stalled ? "stalled" : ""}`} draggable={!ro && !busy}
                    onDragStart={(e) => e.dataTransfer?.setData("text/plain", a.id)} onClick={() => go(`applications/${a.id}`)}>
                    <strong>{a.providers?.first_name} {a.providers?.last_name}</strong>
                    <div class="small">{a.payers?.name}</div>
                    <div class="small muted">
                      {a.submitted_on ? `submitted ${daysSince(a.submitted_on)}d ago` : ""}
                      {stalled ? ` · stalled ${daysSince(a.last_event_at)}d` : ""}
                      {name(a.assignee) ? ` · ${name(a.assignee)}` : ""}
                    </div>
                    {data.est.get(a.id)?.expected_decision_on && <div class="small">expected <Expected a={a} /></div>}
                    {!ro && <select class="small" aria-label="Move to" value={a.status} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => move(a, e.currentTarget.value)}>
                      {APP_STATUSES.map((s) => <option value={s}>{s.replaceAll("_", " ")}</option>)}</select>}
                  </article>);
              })}
            </section>);
        })}</div>
      )}
      {openId && <ApplicationModal ctx={ctx} id={openId} onClose={() => { go("applications"); reload(); }} />}
    </Card>
  );
}

function ApplicationModal({ ctx, id, onClose }: { ctx: Ctx; id: string; onClose: () => void }) {
  const ro = ctx.role === "viewer";
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () => {
    const [a, ev, drafts, ents, org, members] = await Promise.all([
      sb().from("applications").select(SELECT).eq("id", id).single(),
      sb().from("application_events").select("*").eq("application_id", id).order("occurred_at", { ascending: false }),
      sb().from("email_drafts").select("*").eq("application_id", id).neq("status", "discarded").order("created_at", { ascending: false }),
      sb().from("legal_entities").select("id, legal_name").eq("org_id", ctx.orgId),
      sb().from("org_members").select("display_name").eq("org_id", ctx.orgId).eq("user_id", ctx.userId).maybeSingle(),
      sb().from("org_members").select("user_id, display_name, email").eq("org_id", ctx.orgId).eq("active", true).neq("role", "viewer"),
    ]);
    return { a: must(a) as Application, ev: must(ev) as any[], drafts: must(drafts) as any[], ents: must(ents) as any[],
      me: (must(org) as { display_name: string | null } | null)?.display_name ?? ctx.email, members: must(members) as any[] };
  }, [id]);

  if (error) return <Modal title="Application" onClose={onClose}><ErrorBox error={error} /></Modal>;
  if (!data) return <Modal title="Application" onClose={onClose}><Loading /></Modal>;
  const { a } = data;

  const save = (e: Event) => {
    const f = formData(e);
    const backdate = (e.currentTarget as HTMLFormElement).backdate_requested.checked;
    run(async () => {
      must(await sb().from("applications").update({
        status: f.status, entity_id: f.entity_id, submitted_on: f.submitted_on, payer_reference_no: f.payer_reference_no,
        payer_contact_email: f.payer_contact_email, next_follow_up_on: f.next_follow_up_on, approved_on: f.approved_on,
        effective_on: f.effective_on, backdate_requested: backdate, backdate_requested_to: backdate ? f.backdate_requested_to : null,
        backdate_outcome: backdate ? f.backdate_outcome : null, recred_due_on: f.recred_due_on, blocked_reason: f.blocked_reason, notes: f.notes,
        assignee: f.assignee,
      }).eq("id", a.id));
      reload();
    }, "Saved");
  };

  const newDraft = () => run(async () => {
    const d = followUpDraft({
      providerName: `${a.providers?.first_name} ${a.providers?.last_name}`, providerNpi: a.providers?.npi, caqhId: a.providers?.caqh_id,
      payerName: a.payers?.name ?? "", payerReferenceNo: a.payer_reference_no, submittedOn: a.submitted_on,
      entityName: a.legal_entities?.legal_name, groupNpi: a.legal_entities?.group_npi, tinLast4: a.legal_entities?.tin_last4,
      daysSinceSubmitted: a.submitted_on ? Math.round((Date.now() - new Date(`${a.submitted_on}T12:00:00`).getTime()) / 86400000) : null,
      senderName: data.me, orgName: ctx.orgName, backdateRequestedTo: a.backdate_requested ? a.backdate_requested_to : null,
    });
    must(await sb().from("email_drafts").insert({ org_id: a.org_id, application_id: a.id, to_address: a.payer_contact_email, subject: d.subject, body: d.body }));
    reload();
  }, "Draft created — review it, then send it yourself");

  return (
    <Modal title={`${a.providers?.first_name} ${a.providers?.last_name} — ${a.payers?.name}`} onClose={onClose}>
      <p class="small"><a href={`#/payers/${a.payers?.slug}`}>Payer requirements & contacts →</a> · <a href={`#/providers/${a.provider_id}`}>BCBA page →</a></p>
      <form class="grid3" onSubmit={save}>
        <label>Status<select name="status" value={a.status} disabled={ro}>{APP_STATUSES.map((s) => <option value={s}>{s.replaceAll("_", " ")}</option>)}</select></label>
        <label>Billing entity<select name="entity_id" value={a.entity_id ?? ""} disabled={ro}><option value="">—</option>{data.ents.map((e) => <option value={e.id}>{e.legal_name}</option>)}</select></label>
        <label>Assigned to<select name="assignee" value={a.assignee ?? ""} disabled={ro}><option value="">Nobody</option>
          {data.members.map((m) => <option value={m.user_id}>{m.display_name || m.email}{m.user_id === ctx.userId ? " (me)" : ""}</option>)}</select></label>
        <label>Payer reference #<input name="payer_reference_no" value={a.payer_reference_no ?? ""} disabled={ro} /></label>
        <label>Submitted on<input type="date" name="submitted_on" value={a.submitted_on ?? ""} disabled={ro} /></label>
        <label>Next follow-up<input type="date" name="next_follow_up_on" value={a.next_follow_up_on ?? ""} disabled={ro} /></label>
        <label>Payer contact email<input type="email" name="payer_contact_email" value={a.payer_contact_email ?? ""} disabled={ro} /></label>
        <label>Approved on<input type="date" name="approved_on" value={a.approved_on ?? ""} disabled={ro} /></label>
        <label>Effective date<input type="date" name="effective_on" value={a.effective_on ?? ""} disabled={ro} /></label>
        <label>Recredential due<input type="date" name="recred_due_on" value={a.recred_due_on ?? ""} disabled={ro} /></label>
        <label class="inline"><input type="checkbox" name="backdate_requested" checked={a.backdate_requested} disabled={ro} /> Backdating requested</label>
        <label>Requested effective date<input type="date" name="backdate_requested_to" value={a.backdate_requested_to ?? ""} disabled={ro} /></label>
        <label>Backdate outcome<select name="backdate_outcome" value={a.backdate_outcome ?? "pending"} disabled={ro}>
          {["pending", "granted", "partial", "denied"].map((o) => <option value={o}>{o}</option>)}</select></label>
        <label class="span3">Blocked reason (required when status is “blocked”)<input name="blocked_reason" value={a.blocked_reason ?? ""} disabled={ro} /></label>
        <label class="span3">Notes<textarea name="notes" rows={2} disabled={ro}>{a.notes ?? ""}</textarea></label>
        {!ro && <div class="span3 row-end"><button disabled={busy}>Save changes</button></div>}
      </form>
      <p class="muted small">Submitting to the payer is always done by a person in the payer's own portal or by mail. This tool never submits or attests.</p>

      <h3>Follow-up emails {!ro && <button class="small" disabled={busy} onClick={newDraft}>Draft follow-up</button>}</h3>
      {data.drafts.length === 0 && <p class="muted small">No drafts yet. Follow-up tasks appear automatically at day 7, 14, 30 and 60 after submission; click “Draft follow-up” to write one.</p>}
      {data.drafts.map((d) => <DraftEditor draft={d} ro={ro} reload={reload} />)}

      <h3>Timeline</h3>
      {!ro && <form class="row" onSubmit={(e) => {
        const f = formData(e);
        const form = e.currentTarget as HTMLFormElement;
        run(async () => {
          must(await sb().from("application_events").insert({ org_id: a.org_id, application_id: a.id, kind: f.kind, summary: f.summary, contact_name: f.contact_name }));
          form.reset(); reload();
        }, "Logged");
      }}>
        <select name="kind" aria-label="Event type">{["call", "email", "portal_check", "document_sent", "note"].map((k) => <option value={k}>{k.replace("_", " ")}</option>)}</select>
        <input name="summary" required placeholder="What happened? (e.g. Called PR line, told 30 more days)" class="grow" />
        <input name="contact_name" placeholder="Rep name / call ref" />
        <button disabled={busy}>Log</button>
      </form>}
      <ul class="timeline">{data.ev.map((e) => (
        <li><span class="muted small">{new Date(e.occurred_at).toLocaleString()}</span> <Badge value={e.kind} /> {e.summary}
          {e.contact_name && <span class="muted"> — {e.contact_name}</span>}</li>))}</ul>
    </Modal>
  );
}

function DraftEditor({ draft, ro, reload }: { draft: any; ro: boolean; reload: () => void }) {
  const [to, setTo] = useState(draft.to_address ?? "");
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const { busy, run } = useAction();
  const update = (patch: Record<string, unknown>, msg: string) =>
    run(async () => { must(await sb().from("email_drafts").update(patch).eq("id", draft.id)); reload(); }, msg);
  return (
    <div class="draft">
      <div class="row"><Badge value={draft.status} /><span class="muted small">created {fmtDate(draft.created_at)}</span></div>
      <label>To<input value={to} onInput={(e) => setTo(e.currentTarget.value)} disabled={ro} placeholder="payer provider-relations email" /></label>
      <label>Subject<input value={subject} onInput={(e) => setSubject(e.currentTarget.value)} disabled={ro} /></label>
      <label>Body<textarea rows={10} value={body} onInput={(e) => setBody(e.currentTarget.value)} disabled={ro} /></label>
      {!ro && <div class="row-end btns">
        <button class="ghost" disabled={busy} onClick={() => update({ to_address: to || null, subject, body }, "Draft saved")}>Save draft</button>
        <button class="ghost" onClick={() => navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`).then(() => toast("Copied"))}>Copy</button>
        <a class="button ghost" href={mailtoHref(to, subject, body)}>Open in email app</a>
        <button disabled={busy} onClick={() => update({ status: "marked_sent", marked_sent_at: new Date().toISOString(), to_address: to || null, subject, body }, "Marked as sent")}>I sent it</button>
        <button class="ghost danger" disabled={busy} onClick={() => update({ status: "discarded" }, "Discarded")}>Discard</button>
      </div>}
    </div>
  );
}
